import { useCallback, useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
import { MotionPathPlugin } from 'gsap/dist/MotionPathPlugin';

import { observeElementResize } from '#/lib/observe-element-resize';
import { COLORS } from '#/lib/utils';

import { TranscendLogo } from '../TranscendLogo';
import { AnimatedBorderGenerator } from './AnimationAtoms/AnimatedPathGenerator';
import { AvatarBlock } from './AnimationAtoms/AvatarBlock';
import { CardIconBlock } from './AnimationAtoms/CardIconBlock';
import { DataWarehouseIconStack } from './AnimationAtoms/DataWarehouseIconStack';
import { InitiativesBlock } from './AnimationAtoms/InitiativesBlock';
import {
  addBorderAnimation,
  fastIconStackDurations,
  LINE_DRAW_SPEED,
} from './animationUtils';
import { cardIconBlocks } from './content';
import { usePauseTimelineWhenOffscreen } from './usePauseTimelineWhenOffscreen';

gsap.registerPlugin(DrawSVGPlugin, MotionPathPlugin);

export function TabletAnimation() {
  const timelineRef = useRef<GSAPTimeline>(null);
  const container = useRef<HTMLDivElement>(null);
  const [activeButton, setActiveButton] = useState(0);

  // Pause DrawSVG work while offscreen so nav/CTA clicks aren't competing
  // with an infinite GSAP loop on the main thread.
  const isVisibleRef = usePauseTimelineWhenOffscreen(container, timelineRef);

  // Function to restart the animation
  const restartAnimation = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    const q = gsap.utils.selector(container.current);
    // Set all drawable elements to be hidden initially

    timelineRef.current = gsap
      .timeline({
        repeat: -1,
        // Built paused; played below only if already in view, otherwise the
        // offscreen observer starts it on entry.
        paused: true,
        onRepeat: () => {
          setActiveButton((current) => (current === 3 ? 0 : current + 1));
        },
      })
      .set(q('.svg-draw'), { drawSVG: '0%' })
      // Outline behind 4 boxes
      .fromTo(
        `#vertical-line`,
        { drawSVG: '0% 0%' },
        { duration: 0.2, drawSVG: '0% 20%', ease: 'none', delay: 1 },
      )
      .to(
        `#vertical-line`,
        { drawSVG: '80% 100%', duration: 0.4, ease: 'none' },
        '>',
      )
      .to(
        `#vertical-line`,
        { drawSVG: '100% 100%', duration: 0.2, ease: 'none' },
        '>',
      )

      .fromTo(
        [`#outline-top-motion`],
        { drawSVG: '57.5% 57.5%' },
        { duration: 0.3, drawSVG: `47.5% 57.5%`, ease: 'none' },
        'outline',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `37.5% 42.5%`, duration: 0.6, ease: 'none' },
        '>',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `25% 30%`, duration: 0.4, ease: 'none', delay: 0.5 },
        '>',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `10% 20%`, duration: 0.7, ease: 'none', delay: 0.4 },
        '>',
      )
      .to(
        [`#outline-top-motion`],
        { drawSVG: `10% 10%`, duration: 0.3, ease: 'none' },
        '>',
      )
      .fromTo(
        `#outline-bottom-motion`,
        { drawSVG: '10% 10%' },
        { duration: 0.3, drawSVG: `10% 20%`, ease: 'none' },
        'outline',
      )
      .to(
        [`#outline-bottom-motion`],
        { drawSVG: `25% 30%`, duration: 0.6, ease: 'none' },
        '>',
      )
      .to(
        [`#outline-bottom-motion`],
        { drawSVG: `37.5% 42.5%`, duration: 0.4, ease: 'none', delay: 0.5 },
        '>',
      )
      .to(
        [`#outline-bottom-motion`],
        { drawSVG: `47.5% 57.5%`, duration: 0.7, ease: 'none', delay: 0.4 },
        '>',
      )
      .to(
        [`#outline-bottom-motion`],
        { drawSVG: `57.5% 57.5%`, duration: 0.3, ease: 'none' },
        '>',
      );

    //  card borders above outline
    addBorderAnimation(
      timelineRef.current,
      'cardBorder1',
      'box1',
      {
        ...fastIconStackDurations,
        tlPosition: 'outline+=0.7',
        startPosition: 'top',
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder2',
      'box1',
      {
        ...fastIconStackDurations,
        tlPosition: 'outline+=0.7',
        startPosition: 'top',
      },
      q,
    );
    addBorderAnimation(
      timelineRef.current,
      'cardBorder3',
      'box1',
      {
        ...fastIconStackDurations,
        tlPosition: 'outline+=1.5',
        startPosition: 'top',
      },
      q,
    );

    addBorderAnimation(
      timelineRef.current,
      'cardBorder4',
      'box1',
      {
        ...fastIconStackDurations,
        tlPosition: 'outline+=1.5',
        startPosition: 'top',
      },
      q,
    );

    timelineRef.current
      .addLabel('drawCurves', 'outline+=2.9')
      .fromTo(
        `#vertical-line-btm`,
        { drawSVG: '0% 0%' },
        { duration: 0.15, drawSVG: '0% 50%', ease: 'none' },
        'drawCurves',
      )
      .to(
        `#vertical-line-btm`,
        { drawSVG: '50% 100%', duration: 0.3, ease: 'none' },
        '>',
      )
      .to(
        `#vertical-line-btm`,
        { drawSVG: '100% 100%', duration: 0.15, ease: 'none' },
        '>',
      )
      .addLabel('finalBtns', '>');

    // // bottom card
    addBorderAnimation(
      timelineRef.current,
      'initiativesBlock',
      'btns',
      {
        ...fastIconStackDurations,
        tlPosition: 'finalBtns',
        startPosition: { topStart: 60, topEnd: 114 },
      },
      q,
    );

    timelineRef.current.timeScale(LINE_DRAW_SPEED);
    if (isVisibleRef.current) timelineRef.current.play();
  }, [isVisibleRef]);

  // Robust resize listener with threshold and debouncing
  useEffect(() => {
    if (!container.current) return;

    let lastWidth = container.current.offsetWidth;
    let timeoutId: NodeJS.Timeout;

    const disconnect = observeElementResize(container.current, (entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const widthDifference = Math.abs(width - lastWidth);

        // Only restart if width changed by more than 10px
        if (widthDifference > 10) {
          lastWidth = width;

          // Debounce to prevent multiple rapid fires
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            restartAnimation();
          }, 100);
        }
      }
    });

    return () => {
      disconnect();
      clearTimeout(timeoutId);
    };
  }, [restartAnimation]);

  useGSAP(
    () => {
      restartAnimation();
      return () => {
        timelineRef.current?.kill();
      };
    },
    { scope: container },
  );
  return (
    <div ref={container} className="flex flex-col items-center justify-center">
      <AvatarBlock activeButton={activeButton} />
      <div className="flex flex-col items-center">
        <div className="relative h-[150px]">
          <div className="flex justify-center h-[150px] w-[2px] absolute top-0 left-1/2 -translate-x-1/2">
            <svg
              viewBox="0 0 2 150"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
            >
              <path
                d="M0.927368 0.13623L0.927367 150"
                stroke={COLORS.grayscale['03']}
              />
              <path
                d="M0.927368 0.13623L0.927367 150"
                className="stroke-electric-04 svg-draw"
                id="vertical-line"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div className="flex justify-center items-center h-full">
            <div className="bg-charcoal rounded-full p-15 max-w-max max-h-max z-2">
              <TranscendLogo className="w-55" iconOnly />
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="absolute w-[calc(100%-190px)] ml-[95px] rounded-60 h-full">
            <AnimatedBorderGenerator
              topMotionId="outline-top-motion"
              bottomMotionId="outline-bottom-motion"
              borderRadius={60}
              strokeWidth={1}
              strokeColor={COLORS.grayscale['03']}
            />
          </div>
          <div className="flex flex-row h-full relative pt-60 pb-40 items-center flex-wrap gap-y-40">
            <div className="w-1/2">
              <CardIconBlock {...cardIconBlocks.ai} />
            </div>
            <div className="w-1/2">
              <CardIconBlock
                text="Data warehouses"
                iconStack={<DataWarehouseIconStack height="34" />}
                iconBlockId="cardBorder2"
                className="ml-auto"
              />
            </div>
            <div className="w-1/2">
              <CardIconBlock {...cardIconBlocks.marketingAdTech} />
            </div>

            <div className="w-1/2">
              <CardIconBlock
                className="ml-auto"
                {...cardIconBlocks.engagementPlatforms}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center h-[100px] w-[2px]">
        <svg
          viewBox="0 0 2 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0.927368 0.13623L0.927367 100"
            stroke={COLORS.grayscale['03']}
          />
          <path
            d="M0.927368 0.13623L0.927367 100"
            className="stroke-electric-04 svg-draw"
            id="vertical-line-btm"
            strokeWidth={2}
          />
        </svg>
      </div>
      <InitiativesBlock iconBlockId="initiativesBlock" />
    </div>
  );
}
